import { getRawVariations } from "~/composables/global";
import type { ShapePrefix, Variation, Shape } from "~/types/grid.types";
import type { ColorChoice } from "./colors";
import { isWoodSlatShapeId, matchWoodSlatVariation } from "./woodSlats";
import { slatShapeKey } from '~/shared/slats';
import { isSameColor } from '~/shared/layeredEngraving';

let variations: Record<ShapePrefix, Variation[]> | null = null;
let variationsPromise: Promise<Record<ShapePrefix, Variation[]>> | null = null;
let catalogRevision = 0;
export const getVariationCatalogRevision = () => catalogRevision;

// The peak tiles were renamed (PeakLarge → LargeCenterPeak, PeakLargeOffset →
// LargeUpperPeak, PeakSmall → SmallPeak). Existing variation labels in the DB
// still use the old prefixes (e.g. "PeakLargeOffset-Ebony"), so matching must
// understand both spellings until the stored labels are migrated/re-imported.
const LEGACY_PREFIX_ALIASES: Record<string, ShapePrefix> = {
  PeakSmall: "SmallPeak",
  PeakLargeOffset: "LargeUpperPeak",
  PeakLarge: "LargeCenterPeak",
};

// Canonical prefix → every legacy spelling that maps to it.
const CANONICAL_TO_LEGACY: Partial<Record<ShapePrefix, string[]>> = Object.entries(
  LEGACY_PREFIX_ALIASES,
).reduce((acc, [legacy, canonical]) => {
  (acc[canonical] ??= []).push(legacy);
  return acc;
}, {} as Partial<Record<ShapePrefix, string[]>>);

// All prefix spellings to try for a shape's prefix: the prefix itself plus any
// legacy spellings that were renamed to it.
const prefixSpellings = (prefix: ShapePrefix): string[] => [
  prefix,
  ...(CANONICAL_TO_LEGACY[prefix] ?? []),
];

export const loadVariations = async (force = false) => {
  if (force) variations = null;
  if (variations) return variations;
  if (variationsPromise) return variationsPromise;
  variationsPromise = parseVariations();
  try {
    variations = await variationsPromise;
    catalogRevision++;
    return variations;
  } finally {
    // A failed catalog fetch must not poison every subsequent retry.
    variationsPromise = null;
  }
};

export const getLoadedVariationsForPrefix = (prefix: ShapePrefix): readonly Variation[] =>
  variations?.[prefix] ?? [];

async function parseVariations(): Promise<Record<ShapePrefix, Variation[]>> {
  const data = (await getRawVariations()) as Variation[];

  // size, shape, color — shape prefix appears first in the variation label
  const pattern1: Partial<Record<ShapePrefix | string, Variation[]>> = {
    LargeHalfCircle: [],
    QuarterCircle: [],
    SmallHalfCircle: [],
    HalfCircle: [],
    LargeSquare: [],
    Quarter: [],
    Pixel: [],
    Bit: [],
    LargeHalflap: [],
    Half: [],
    MiniShiplap: [],
    Shiplap: [],
    LargeTriangle: [],
    Small90Triangle: [],
    SmallTriangle: [],
    Triangle: [],
    Tangent: [],
    CrissCross: [],
    Knot: [],
    Converge: [],
    Topo: [],
    SmallPeak: [],
    LargeUpperPeak: [],
    LargeCenterPeak: [],
    WoodSlat4Ft: [],
    WoodSlat8Ft: [],
    // Legacy peak prefixes — still present in stored variation labels. These
    // buckets are merged into their canonical keys after matching (see below).
    PeakLargeOffset: [],
    PeakLarge: [],
    PeakSmall: [],
    Offset: [],
    Arc: [],
    Hex: [],
    LargeDiamond: [],
    Diamond: [],
    LargeParallel: [],
    River: [],
    Subway: [],
    Zen: [],
    Aspen: [],
    Bamboo: [],
    Geo: [],
    Pinstripe: [],
    Blossom: [],
    Strand: [],
    Cube36x36x2: [],
    Cube36x36x4: [],
    Cube12x12x2: [],
    Cube12x12x4: [],
    Rect12x36x2: [],
    Rect12x36x4: [],
    // 3D Engraved
    Cube36x36x4LargeParallel: [],
    Cube36x36x2LargeParallel: [],
    Cube36x36x4Tangent: [],
    Cube36x36x2Tangent: [],
    Cube36x36x4Zen: [],
    Cube36x36x2Zen: [],
    Cube36x36x4River: [],
    Cube36x36x2River: [],
    Cube36x36x4Offset: [],
    Cube36x36x2Offset: [],
    Cube36x36x4Topo: [],
    Cube36x36x2Topo: [],
    Cube36x36x4Converge: [],
    Cube36x36x2Converge: [],
    Cube36x36x4Subway: [],
    Cube36x36x2Subway: [],
  };
  // color, shape
  const pattern2: Partial<Record<ShapePrefix, Variation[]>> = {
    Blank: [],
    Slash: [],
    Cross: [],
    "4Square": [],
    Parallel: [],
  };

  const unassignedVariations: Variation[] = [];

  // Sort keys by length descending so longer prefixes match first
  // (e.g. "LargeCenterPeak" matches before "Center")
  const pattern1Keys = Object.keys(pattern1).sort((a, b) => b.length - a.length);
  const pattern2Keys = Object.keys(pattern2).sort((a, b) => b.length - a.length);

  data.forEach((v: Variation) => {
    if (v.layered_engraving) {
      const prefix = `Layered${v.layered_engraving.pattern}`;
      (pattern1[prefix] ??= []).push(v);
      return;
    }
    const woodSlatPrefix = slatShapeKey(v);
    if (woodSlatPrefix) {
      pattern1[woodSlatPrefix]?.push(v);
      return;
    }

    const pattern1Key = pattern1Keys.find((prefix) =>
      v.label.includes(prefix),
    ) as ShapePrefix | undefined;
    if (pattern1Key && pattern1[pattern1Key]) {
      pattern1[pattern1Key].push(v);
      return;
    }

    const pattern2Key = pattern2Keys.find((prefix) =>
      v.label.includes(prefix),
    ) as ShapePrefix | undefined;
    if (pattern2Key && pattern2[pattern2Key]) {
      pattern2[pattern2Key].push(v);
      return;
    }

    unassignedVariations.push(v);
  });

  // Merge legacy peak buckets into their canonical keys so that
  // getColorVariation(shape) — which uses the new shape.prefix — finds them.
  for (const [legacyKey, canonicalKey] of Object.entries(LEGACY_PREFIX_ALIASES)) {
    const legacy = pattern1[legacyKey]
    const canonical = pattern1[canonicalKey as ShapePrefix]
    if (legacy?.length && canonical) {
      canonical.push(...legacy)
    }
    delete pattern1[legacyKey]
  }

  return { ...pattern1, ...pattern2 } as Record<ShapePrefix, Variation[]>;
}

export const getColorVariation = (
  shape: Shape,
  color: ColorChoice,
  options: { allowFallback?: boolean } = {},
): Variation | undefined => {
  try {
    if (!variations) {
      throw new Error(
        "Variations not parsed, please call loadVariations() first",
      );
    }
    if (!color || !shape) throw new Error("No color or shape selected");

    if (!variations[shape.prefix] || variations[shape.prefix].length === 0) {
      console.error(`❌ No variations found for shape prefix: ${shape.prefix}`);
      console.error("Available prefixes:", Object.keys(variations));
      return undefined;
    }

    if (shape.finishFamily === 'layered-engraved') {
      return variations[shape.prefix].find(v =>
        isSameColor(v.layered_engraving?.top_color, color.label) ||
        isSameColor(v.label.replace(/^Layered[^-]+-/, ''), color.label),
      );
    }
    const normalizedColorLabel = color.label.replace(/\s+/g, "");

    const WOOD_GRAIN_LABELS = ['Dark Ash', 'Light Ash', 'Medium Elm', 'Natural Walnut', 'White Oak'];
    if (WOOD_GRAIN_LABELS.includes(color.label)) {
      const isBlank3D = shape.prefix === 'Cube36x36x4' || shape.prefix === 'Cube36x36x2' ||
        shape.prefix === 'Cube12x12x4' || shape.prefix === 'Cube12x12x2' ||
        shape.prefix === 'Rect12x36x4' || shape.prefix === 'Rect12x36x2';
      if (!isBlank3D) {
        return undefined;
      }
    }

    // Try the canonical prefix and any legacy spelling still on stored labels
    // (e.g. shape.prefix "LargeUpperPeak" also matches "PeakLargeOffset-Ebony").
    const spellings = prefixSpellings(shape.prefix);

    if (isWoodSlatShapeId(shape.id)) {
      return matchWoodSlatVariation(variations[shape.prefix], shape.id, color.label);
    }

    // size-shape-color: "{prefix}-{color}"
    let variation = variations[shape.prefix].find((v) => {
      const normalizedLabel = v.label.replace(/\s+/g, "");
      return spellings.some(
        (p) =>
          v.label === `${p}-${color.label}` ||
          normalizedLabel === `${p}-${normalizedColorLabel}`,
      );
    });
    if (variation) return variation;

    // color-shape: "{color}-{prefix}"
    variation = variations[shape.prefix].find((v) => {
      const normalizedLabel = v.label.replace(/\s+/g, "");
      return spellings.some(
        (p) =>
          v.label === `${color.label}-${p}` ||
          normalizedLabel === `${normalizedColorLabel}-${p}`,
      );
    });
    if (variation) return variation;

    if (options.allowFallback === false) return undefined;

    const fallback = variations[shape.prefix][0];
    if (fallback) {
      if (spellings.some((p) => fallback.label.includes(p))) {
        console.warn(
          `⚠️  Using fallback for ${shape.prefix}-${color.label}: ${fallback.label}`,
        );
        return fallback;
      } else {
        console.error(
          `❌ Fallback mismatch! Expected ${shape.prefix} but got ${fallback.label}`,
        );
        console.error(
          `Available variations for ${shape.prefix}:`,
          variations[shape.prefix].map((v) => v.label),
        );
      }
    }

    throw new Error(
      `No valid variation found for shape: ${shape.prefix}, color: ${color.label}`,
    );
  } catch (error) {
    console.error("❌ getColorVariation error:", error);
    console.error("Shape:", shape);
    console.error("Color:", color);
    return undefined;
  }
};

export const debugVariationLookup = async (
  shapePrefix: string,
  colorLabel: string,
) => {
  if (!variations) await loadVariations();

  console.log(`🔍 DEBUG: Testing ${shapePrefix} + ${colorLabel}`);
  console.log(
    `Available variations for ${shapePrefix}:`,
    variations?.[shapePrefix as ShapePrefix]?.map((v) => v.label) || "None",
  );

  const pattern1 = `${shapePrefix}-${colorLabel}`;
  const pattern2 = `${colorLabel}-${shapePrefix}`;

  const found1 = variations?.[shapePrefix as ShapePrefix]?.find(
    (v) => v.label === pattern1,
  );
  const found2 = variations?.[shapePrefix as ShapePrefix]?.find(
    (v) => v.label === pattern2,
  );

  if (found1) console.log(`✅ Pattern 1 match:`, found1.label);
  if (found2) console.log(`✅ Pattern 2 match:`, found2.label);
  if (!found1 && !found2) {
    console.warn(`❌ No matches found`);
    console.log(
      `Fallback would be:`,
      variations?.[shapePrefix as ShapePrefix]?.[0]?.label || "None",
    );
  }
};
